import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Button, CircularProgress } from "@mui/material";
import React from "react";

interface LeadContactsFooterProps {
  selectedLeads: Set<number>;
  loading: boolean;
  paginationLoading: boolean;
  page: number;
  totalPages: number;
  onCopyBatch: () => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onPageSelect: (page: number) => void;
}

const LeadContactsFooter: React.FC<LeadContactsFooterProps> = ({
  selectedLeads,
  loading,
  paginationLoading,
  page,
  totalPages,
  onCopyBatch,
  onPreviousPage,
  onNextPage,
  onPageSelect,
}) => {
  return (
    <Box sx={{ flexShrink: 0 }}>
      {/* Botão de cópia em lote */}
      {selectedLeads.size > 0 && (
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fff",
          }}
        >
          <Button
            variant="contained"
            fullWidth
            startIcon={<ContentCopyIcon />}
            onClick={onCopyBatch}
            sx={{
              backgroundColor: "#008069",
              "&:hover": {
                backgroundColor: "#006d5b",
              },
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Copiar {selectedLeads.size} Lead
            {selectedLeads.size !== 1 ? "s" : ""} Selecionado
            {selectedLeads.size !== 1 ? "s" : ""}
          </Button>
        </Box>
      )}

      {/* Navegação entre páginas */}
      {totalPages > 0 && (
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              onClick={onPreviousPage}
              disabled={loading || paginationLoading || page <= 1}
              style={{
                background:
                  page <= 1 || paginationLoading ? "#f5f5f5" : "#00a884",
                color: page <= 1 || paginationLoading ? "#999" : "white",
                border:
                  page <= 1 || paginationLoading ? "1px solid #e0e0e0" : "none",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor:
                  page <= 1 || paginationLoading ? "not-allowed" : "pointer",
                fontSize: "12px",
                fontWeight: "500",
                transition: "all 0.2s ease",
                opacity: page <= 1 || paginationLoading ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onMouseOver={(e) => {
                if (page > 1 && !loading && !paginationLoading) {
                  e.currentTarget.style.backgroundColor = "#008f6f";
                }
              }}
              onFocus={(e) => {
                if (page > 1 && !loading && !paginationLoading) {
                  e.currentTarget.style.backgroundColor = "#008f6f";
                }
              }}
              onMouseOut={(e) => {
                if (page > 1 && !loading && !paginationLoading) {
                  e.currentTarget.style.backgroundColor = "#00a884";
                }
              }}
              onBlur={(e) => {
                if (page > 1 && !loading && !paginationLoading) {
                  e.currentTarget.style.backgroundColor = "#00a884";
                }
              }}
            >
              {paginationLoading && page > 1 ? (
                <>
                  <CircularProgress size={12} sx={{ color: "white" }} />
                  Carregando...
                </>
              ) : (
                "← Anterior"
              )}
            </button>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "12px",
                color: "#666",
              }}
            >
              <span>Página</span>
              <select
                value={page}
                onChange={(e) => onPageSelect(Number(e.target.value))}
                disabled={loading || paginationLoading}
                style={{
                  padding: "6px 8px",
                  borderRadius: "6px",
                  border: "1px solid #e0e0e0",
                  fontSize: "12px",
                  backgroundColor:
                    loading || paginationLoading ? "#f5f5f5" : "white",
                  color: "#333",
                  cursor:
                    loading || paginationLoading ? "not-allowed" : "pointer",
                }}
              >
                {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(
                  (pageNumber) => (
                    <option key={pageNumber} value={pageNumber}>
                      {pageNumber}
                    </option>
                  ),
                )}
              </select>
              <span>de {totalPages}</span>
            </div>

            <button
              onClick={onNextPage}
              disabled={loading || paginationLoading || page >= totalPages}
              style={{
                background:
                  page >= totalPages || paginationLoading
                    ? "#f5f5f5"
                    : "#00a884",
                color:
                  page >= totalPages || paginationLoading ? "#999" : "white",
                border:
                  page >= totalPages || paginationLoading
                    ? "1px solid #e0e0e0"
                    : "none",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor:
                  page >= totalPages || paginationLoading
                    ? "not-allowed"
                    : "pointer",
                fontSize: "12px",
                fontWeight: "500",
                transition: "all 0.2s ease",
                opacity: page >= totalPages || paginationLoading ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onMouseOver={(e) => {
                if (page < totalPages && !loading && !paginationLoading) {
                  e.currentTarget.style.backgroundColor = "#008f6f";
                }
              }}
              onFocus={(e) => {
                if (page < totalPages && !loading && !paginationLoading) {
                  e.currentTarget.style.backgroundColor = "#008f6f";
                }
              }}
              onMouseOut={(e) => {
                if (page < totalPages && !loading && !paginationLoading) {
                  e.currentTarget.style.backgroundColor = "#00a884";
                }
              }}
              onBlur={(e) => {
                if (page < totalPages && !loading && !paginationLoading) {
                  e.currentTarget.style.backgroundColor = "#00a884";
                }
              }}
            >
              {paginationLoading && page < totalPages ? (
                <>
                  <CircularProgress size={12} sx={{ color: "white" }} />
                  Carregando...
                </>
              ) : (
                "Próximo →"
              )}
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LeadContactsFooter;
